import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Inbox, Loader2, TriangleAlert, UserCheck, UserX } from 'lucide-react';
import { RequireRole } from '../components/RequireRole';
import { inputClass } from '../components/fields';
import {
  approveApplication,
  listApplications,
  rejectApplication,
  type ApplicationRow,
} from '../lib/members';

type Load = 'loading' | 'ready' | 'error';
type Notice = { tone: 'success' | 'error'; text: string } | null;

/**
 * Spec 004 — the board approval queue (SC-401..406). Lists pending applications oldest-first; each can be
 * approved (mints a member, sends a welcome email) or rejected with an optional internal reason. Four
 * states: loading → list → empty ("no pending applications") → error. A row shows its own busy + inline
 * outcome; a welcome-email failure surfaces a specific, non-blocking warning. WCAG 2.1 AA.
 */
function ApprovalQueueInner() {
  const [load, setLoad] = useState<Load>('loading');
  const [rows, setRows] = useState<ApplicationRow[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [notice, setNotice] = useState<Notice>(null);

  const refresh = useCallback(async () => {
    const r = await listApplications(1);
    if (r.kind === 'ok') {
      setRows(r.data.items);
      setLoad('ready');
    } else {
      setLoad('error');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function onApprove(row: ApplicationRow) {
    if (busyId) return;
    setBusyId(row.id);
    setNotice(null);
    const r = await approveApplication(row.id);
    setBusyId(null);
    if (r.kind === 'ok') {
      setRows((rs) => rs.filter((x) => x.id !== row.id));
      setNotice({
        tone: r.data.emailSent ? 'success' : 'error',
        text: r.data.emailSent
          ? `${row.fullName} is now a member. A welcome email is on its way.`
          : `${row.fullName} is now a member, but the welcome email couldn’t be sent. You can resend it from the member register.`,
      });
    } else if (r.kind === 'conflict') {
      setNotice({ tone: 'error', text: r.message });
      void refresh();
    } else if (r.kind === 'forbidden') {
      setNotice({ tone: 'error', text: 'You don’t have permission to approve applications.' });
    } else {
      setNotice({ tone: 'error', text: 'We couldn’t approve that application. Please try again.' });
    }
  }

  async function onReject(row: ApplicationRow) {
    if (busyId) return;
    setBusyId(row.id);
    setNotice(null);
    const r = await rejectApplication(row.id, reason.trim() || undefined);
    setBusyId(null);
    if (r.kind === 'ok') {
      setRows((rs) => rs.filter((x) => x.id !== row.id));
      setRejecting(null);
      setReason('');
      setNotice({ tone: 'success', text: `${row.fullName}’s application was rejected.` });
    } else if (r.kind === 'conflict') {
      setNotice({ tone: 'error', text: r.message });
      void refresh();
    } else {
      setNotice({ tone: 'error', text: 'We couldn’t reject that application. Please try again.' });
    }
  }

  return (
    <div className="mx-auto w-full max-w-3xl px-6 py-16 sm:py-20">
      <p className="font-display text-xs font-semibold uppercase tracking-[0.28em] text-accent-deep">Board</p>
      <h1 className="mt-3 font-display text-3xl font-semibold uppercase tracking-[0.12em] text-ink sm:text-4xl">
        Pending applications
      </h1>
      <p className="mt-4 font-serif text-lg leading-relaxed text-muted">
        Review each request to join. Approving creates the member and invites them to sign in.
      </p>

      {notice && (
        <p
          role={notice.tone === 'error' ? 'alert' : 'status'}
          className={`mt-6 flex items-start gap-2 rounded-md border px-3 py-2 font-sans text-sm ${
            notice.tone === 'error' ? 'border-error/40 bg-error/5 text-error' : 'border-success/40 bg-success/5 text-success'
          }`}
        >
          {notice.tone === 'error' ? <TriangleAlert aria-hidden className="mt-0.5 h-4 w-4 shrink-0" /> : <CheckCircle2 aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />}
          {notice.text}
        </p>
      )}

      {load === 'loading' && (
        <div className="mt-10 flex flex-col items-center" aria-live="polite">
          <Loader2 aria-hidden className="h-8 w-8 animate-spin text-accent" />
          <p className="mt-4 font-serif text-lg text-ink">Loading applications…</p>
        </div>
      )}

      {load === 'error' && (
        <p role="alert" className="mt-8 flex items-start gap-2 rounded-md border border-error/40 bg-error/5 px-3 py-2 font-sans text-sm text-error">
          <TriangleAlert aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
          We couldn’t load the applications. Please refresh and try again.
        </p>
      )}

      {load === 'ready' && rows.length === 0 && (
        <div className="mt-12 flex flex-col items-center rounded-md border border-dashed border-border bg-surface/40 px-6 py-16 text-center">
          <Inbox aria-hidden className="h-9 w-9 text-muted" />
          <p className="mt-4 font-display text-lg font-semibold uppercase tracking-[0.1em] text-ink">No pending applications</p>
          <p className="mt-2 font-serif text-base text-muted">When someone applies, they’ll appear here for review.</p>
        </div>
      )}

      {load === 'ready' && rows.length > 0 && (
        <ul className="mt-8 space-y-4">
          {rows.map((row) => {
            const busy = busyId === row.id;
            return (
              <li key={row.id} className="rounded-md border border-border bg-surface p-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-display text-lg font-semibold tracking-wide text-ink">{row.fullName}</p>
                    <p className="break-all font-sans text-sm text-muted">{row.email}</p>
                    <p className="mt-1 font-sans text-xs text-muted">
                      Applied {new Date(row.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <button
                      type="button"
                      onClick={() => onApprove(row)}
                      disabled={busy}
                      className="inline-flex items-center gap-2 rounded-md bg-accent px-3.5 py-2 font-sans text-sm font-semibold text-bg transition-colors duration-150 ease-standard hover:bg-accent-deep focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-60"
                    >
                      {busy ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : <UserCheck aria-hidden className="h-4 w-4" />}
                      Approve
                    </button>
                    <button
                      type="button"
                      onClick={() => { setRejecting(rejecting === row.id ? null : row.id); setReason(''); }}
                      disabled={busy}
                      aria-expanded={rejecting === row.id}
                      className="inline-flex items-center gap-2 rounded-md border border-border px-3.5 py-2 font-sans text-sm font-medium text-ink transition-colors duration-150 ease-standard hover:bg-bg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-60"
                    >
                      <UserX aria-hidden className="h-4 w-4" />
                      Reject
                    </button>
                  </div>
                </div>

                {row.message && (
                  <p className="mt-3 border-t border-border/60 pt-3 font-serif text-sm leading-relaxed text-ink">{row.message}</p>
                )}

                {rejecting === row.id && (
                  <div className="mt-4 border-t border-border/60 pt-4">
                    <label htmlFor={`reason-${row.id}`} className="block font-sans text-sm font-medium text-ink">
                      Reason <span className="font-normal text-muted">(optional, internal — not sent to the applicant)</span>
                    </label>
                    <textarea
                      id={`reason-${row.id}`}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      rows={2}
                      maxLength={500}
                      className={`${inputClass} mt-1.5 resize-y`}
                    />
                    <button
                      type="button"
                      onClick={() => onReject(row)}
                      disabled={busy}
                      className="mt-3 inline-flex items-center gap-2 rounded-md border border-error/50 px-3.5 py-2 font-sans text-sm font-semibold text-error transition-colors duration-150 ease-standard hover:bg-error/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-error disabled:opacity-60"
                    >
                      {busy && <Loader2 aria-hidden className="h-4 w-4 animate-spin" />}
                      Confirm rejection
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default function ApprovalQueue() {
  return <RequireRole min="board">{() => <ApprovalQueueInner />}</RequireRole>;
}
